import React, { useEffect, useState, useRef } from 'react';
import { glyphPaths } from '../icons/icon-paths';
import { Canvg } from 'canvg';
import { usePageTitle } from '../hooks/usePageTitle';
import '../utils/glyph-page.css';

export default function GlyphPage() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [glyphGrid, setGlyphGrid] = useState(null);
  const [animationPhase, setAnimationPhase] = useState('idle'); // 'idle', 'toPosition', 'pausing', 'toG11', 'toClear'
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showImage, setShowImage] = useState(true);
  const [imageVisible, setImageVisible] = useState(false);
  const [imageOpacity, setImageOpacity] = useState(0);
  const [contrast, setContrast] = useState(1.0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [isCapturingFrames, setIsCapturingFrames] = useState(false);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [recordingMode, setRecordingMode] = useState(false); // New state for recording mode
  const animationStartTimeRef = useRef(null); // Track animation start time

  const canvasRef = useRef(null);
  const glyphStateRef = useRef([]);
  const glyphImgsRef = useRef({});
  const animationRef = useRef(null);
  const targetGridRef = useRef(null);
  const activeImageRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const capturedFramesRef = useRef([]);
  const isCapturingFramesRef = useRef(false); // Add ref for frame capture state
  const isRecordingSessionRef = useRef(false); // Add ref to track if this is a recording session
  const [canvasStyle, setCanvasStyle] = useState({});

  const CELL_SIZE = 10;
  const GLYPH_RES = 24;
  const GLYPH_COLOR = "#3520D6";
  const ANIMATION_SPEED = 40;
  const ANIMATION_OUT_SPEED = 40;
  const CROSSFADE_DURATION = 400;
  const GLYPH_PAUSE = 250;

  const glyphIds = [
    'g11', 'g10', 'g09', 'g08', 'g07', 'g06', 'g05', 'g04', 'g03', 'g02', 'g01'
  ];

  // Title management
  usePageTitle({
    title: 'ITORA - Glyph Animation',
    description: 'Upload an image and watch it transform into animated glyphs using our AI-powered visualization technique.',
    url: `${window.location.origin}/glyph`
  });

  const cacheGlyphBitmaps = async () => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = GLYPH_RES;
    tempCanvas.height = GLYPH_RES;
    const ctx = tempCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    for (const id of glyphIds) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" fill="${GLYPH_COLOR}" width="24" height="24" viewBox="0 0 24 24">${glyphPaths[id]}</svg>`;
      const v = await Canvg.fromString(ctx, svg);
      await v.render();

      const outCanvas = document.createElement('canvas');
      outCanvas.width = GLYPH_RES;
      outCanvas.height = GLYPH_RES;
      outCanvas.getContext('2d').drawImage(tempCanvas, 0, 0);
      glyphImgsRef.current[id] = outCanvas;
      ctx.clearRect(0, 0, GLYPH_RES, GLYPH_RES);
    }
  };

  useEffect(() => {
    cacheGlyphBitmaps();
    // Load test image by default
    setImageUrl('/img/abtest/a.png');
  }, []);

  // Remove the conflicting resize effect - canvas should only match image dimensions

  // Update canvas size and position to match the active image
  useEffect(() => {
    if (!activeImageRef.current || !canvasRef.current) return;
    const img = activeImageRef.current;
    const canvas = canvasRef.current;
    
    // Wait for image to load and get its natural dimensions
    if (img.complete && img.naturalWidth > 0) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      canvas.width = width;
      canvas.height = height;
      setCanvasStyle({
        position: 'absolute',
        top: 0,
        left: 0,
        width: img.offsetWidth,
        height: img.offsetHeight,
        pointerEvents: 'none',
        zIndex: 5
      });
    } else {
      // If image not loaded yet, wait for it
      const handleLoad = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        canvas.width = width;
        canvas.height = height;
        setCanvasStyle({
          position: 'absolute',
          top: 0,
          left: 0,
          width: img.offsetWidth,
          height: img.offsetHeight,
          pointerEvents: 'none',
          zIndex: 5
        });
      };
      
      img.addEventListener('load', handleLoad);
      return () => img.removeEventListener('load', handleLoad);
    }
  }, [imageUrl]);

  const drawToBufferAndBlit = (grid) => {
    if (!grid || !canvasRef.current) return;
    const mainCanvas = canvasRef.current;
    const width = mainCanvas.width;
    const height = mainCanvas.height;

    if (width === 0 || height === 0) return;

    const buffer = document.createElement('canvas');
    buffer.width = width;
    buffer.height = height;
    const bctx = buffer.getContext('2d');

    // Fill with white background when recording
    if (isRecording || isRecordingSessionRef.current) {
      bctx.fillStyle = 'white';
      bctx.fillRect(0, 0, width, height);
    }

    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        const glyphIndex = grid[y][x];

        if (glyphIndex === null || glyphIndex === undefined) {
          continue;
        }

        const glyph = glyphImgsRef.current[glyphIds[glyphIndex]];
        if (glyph && glyph.width > 0 && glyph.height > 0) {
          bctx.drawImage(glyph, x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }
      }
    }

    const ctx = mainCanvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, 0, 0);

    glyphStateRef.current = grid;
  };

  const completeRecordingAnimation = () => {
    console.log('Recording animation complete, stopping recording in 2 seconds...');
    
    // Stop recording after 2 seconds to capture the complete clearing animation
    setTimeout(() => {
      console.log('Stopping recording now after 2 second delay...');
      stopRecording();
      // stopFrameCapture(); // Temporarily disabled PNG recording
      isRecordingSessionRef.current = false;
    }, 2000);
  };

  const animateGlyphsToClear = () => {
    if (!glyphStateRef.current || glyphStateRef.current.length === 0) {
      console.warn('Current glyph state not available, skipping animation to clear.');
      return;
    }

    setAnimationPhase('toClear');

    const grid = glyphStateRef.current;
    const totalCells = grid.length * grid[0].length;

    const allPositions = [];
    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        if (grid[y][x] !== null && grid[y][x] !== undefined) {
          allPositions.push({ x, y });
        }
      }
    }

    for (let i = allPositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
    }

    const CLEAR_ANIMATION_SPEED = 40; // Changed from 15 to 40 to match other animations
    const CELLS_PER_STEP = Math.max(1, Math.floor(allPositions.length / 30));
    let currentIndex = 0;

    const animate = () => {
      if (currentIndex >= allPositions.length) {
        const clearedGrid = grid.map(row => row.map(() => null));
        setGlyphGrid(clearedGrid);
        setAnimationPhase('idle');
        setIsAnimating(false);

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        
        // Both recording and regular animations should complete the same way
        const duration = Date.now() - animationStartTimeRef.current;
        console.log('Animation complete at', new Date().toISOString(), 'Duration:', duration + 'ms');
        
        // If this was a recording session, handle recording completion
        if (isRecordingSessionRef.current) {
          console.log('Recording session detected, completing recording...');
          completeRecordingAnimation();
        }
        
        return;
      }

      const newGrid = glyphStateRef.current.map(row => [...row]);

      for (let i = 0; i < CELLS_PER_STEP && currentIndex < allPositions.length; i++) {
        const pos = allPositions[currentIndex];
        newGrid[pos.y][pos.x] = null;
        currentIndex++;
      }

      setGlyphGrid(newGrid);
      
      // Debug: Log animation progress
      if (currentIndex % 50 === 0) {
        console.log(`Clear animation progress: ${currentIndex}/${allPositions.length} (${Math.round(currentIndex/allPositions.length*100)}%) - Recording: ${isRecordingSessionRef.current}`);
      }
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    animationRef.current = setInterval(animate, CLEAR_ANIMATION_SPEED);
  };

  const animateGlyphsToPosition = () => {
    if (!targetGridRef.current) {
      console.warn('Target grid not available, skipping animation to position.');
      return;
    }

    setAnimationPhase('toPosition');
    const maxSteps = glyphIds.length - 1;
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= maxSteps || !targetGridRef.current) {
        setGlyphGrid(targetGridRef.current);
        setAnimationPhase('pausing');

        setTimeout(() => {
          animateGlyphsToG11();
        }, GLYPH_PAUSE);

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const newGrid = targetGridRef.current.map((row, y) =>
        row.map((targetIndex, x) => {
          const currentIndex = glyphStateRef.current[y]?.[x] || 0;
          return Math.min(currentIndex + 1, targetIndex);
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
      
      // Debug: Log animation progress
      if (currentStep % 5 === 0) {
        console.log(`Position animation progress: ${currentStep}/${maxSteps} (${Math.round(currentStep/maxSteps*100)}%) - Recording: ${isRecordingSessionRef.current}`);
      }
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    animationRef.current = setInterval(animate, ANIMATION_SPEED);
  };

  const animateGlyphsToG11 = () => {
    if (!targetGridRef.current) {
      console.warn('Target grid not available, skipping animation to g11.');
      return;
    }

    setAnimationPhase('toG11');

    // Start the crossfade immediately when the animation begins
    setImageVisible(true);

    const maxSteps = glyphIds.length - 1;
    const stepsNeeded = Math.max(1, Math.ceil(CROSSFADE_DURATION / ANIMATION_OUT_SPEED));
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= stepsNeeded || !targetGridRef.current) {
        const g11Grid = targetGridRef.current.map(row => row.map(() => 0));
        setGlyphGrid(g11Grid);
        setAnimationPhase('idle');

        setTimeout(() => {
          animateGlyphsToClear();
        });

        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      const progress = (currentStep + 1) / stepsNeeded;
      const newGrid = targetGridRef.current.map((row, y) =>
        row.map((currentIndex) => {
          const targetIndex = 0;
          const stepsToTarget = currentIndex - targetIndex;
          const stepsToMove = Math.ceil(stepsToTarget * progress);
          return Math.max(currentIndex - stepsToMove, 0);
        })
      );

      setGlyphGrid(newGrid);
      currentStep++;
      
      // Debug: Log animation progress
      if (currentStep % 2 === 0) {
        console.log(`G11 animation progress: ${currentStep}/${stepsNeeded} (${Math.round(currentStep/stepsNeeded*100)}%) - Recording: ${isRecordingSessionRef.current}`);
      }
    };

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    animationRef.current = setInterval(animate, ANIMATION_SPEED);
  };

  const processImage = () => {
    if (!imageUrl) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      const targetGrid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          let brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          
          // Apply contrast adjustment
          brightness = ((brightness / 255 - 0.5) * contrast + 0.5) * 255;
          brightness = Math.max(0, Math.min(255, brightness));
          
          const index = glyphIds.length - 1 - Math.floor((brightness / 255) * (glyphIds.length - 1));
          row.push(index);
        }
        targetGrid.push(row);
      }

      // Store target grid for animations
      targetGridRef.current = targetGrid;

      // Initialize grid with all g11 (index 0) - just show the final result
      const initialGrid = targetGrid.map(row => row.map(() => 0));
      setGlyphGrid(initialGrid);
    };

    img.onload = tryProcess;
    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
      setGlyphGrid(null);
      targetGridRef.current = null;
    };
  };

  const processImageForAnimation = () => {
    if (!imageUrl) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      const targetGrid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          let brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          
          // Apply contrast adjustment
          brightness = ((brightness / 255 - 0.5) * contrast + 0.5) * 255;
          brightness = Math.max(0, Math.min(255, brightness));
          
          const index = glyphIds.length - 1 - Math.floor((brightness / 255) * (glyphIds.length - 1));
          row.push(index);
        }
        targetGrid.push(row);
      }

      // Store target grid for animations
      targetGridRef.current = targetGrid;

      // Initialize grid with all g11 (index 0)
      const initialGrid = targetGrid.map(row => row.map(() => 0));
      setGlyphGrid(initialGrid);

      // Start animation to position immediately
      animateGlyphsToPosition();
    };

    img.onload = tryProcess;
    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
      setGlyphGrid(null);
      targetGridRef.current = null;
    };
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setUploadedImage(file);
      setGlyphGrid(null);
      setAnimationPhase('idle');
      targetGridRef.current = null;
      setRecordingMode(false); // Reset recording mode for new image
      
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set drag over to false if we're leaving the page entirely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setImageUrl(url);
        setUploadedImage(file);
        setGlyphGrid(null);
        setAnimationPhase('idle');
        targetGridRef.current = null;
        setRecordingMode(false); // Reset recording mode for new image
        
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
      }
    }
  };

  const startAnimation = () => {
    if (!imageUrl || isAnimating) return;
    
    console.log('startAnimation called - PLAY button pressed at', new Date().toISOString());
    
    // PLAY button should completely reset all recording state
    setRecordingMode(false);
    setRecordingBlob(null);
    setCapturedFrames([]);
    setIsRecording(false);
    setIsCapturingFrames(false);
    isCapturingFramesRef.current = false;
    isRecordingSessionRef.current = false; // Clear recording session ref
    
    // Clear any existing recording state
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (capturedFramesRef.current) {
      forceStopFrameCapture();
      capturedFramesRef.current = [];
    }
    
    setIsAnimating(true);
    setImageVisible(false);
    setImageOpacity(0); // Set opacity to 0 instantly
    setGlyphGrid(null);
    setAnimationPhase('idle');
    animationStartTimeRef.current = Date.now(); // Track start time
    
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
    
    // Process image first to ensure canvas is set up
    processImageForAnimation();
    
    // PLAY button never starts recording
    console.log('PLAY button - animation only, no recording');
  };

  const startRecordingAnimation = () => {
    if (!imageUrl || isAnimating) return;
    
    console.log('startRecordingAnimation called - RECORD button pressed at', new Date().toISOString());
    
    // Reset all recording state first
    setRecordingMode(false);
    setRecordingBlob(null);
    setCapturedFrames([]);
    setIsRecording(false);
    setIsCapturingFrames(false);
    isCapturingFramesRef.current = false; // Reset frame capture ref
    
    // Clear any existing recording state
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (capturedFramesRef.current) {
      forceStopFrameCapture();
      capturedFramesRef.current = [];
    }
    
    // Now set recording mode and start
    setRecordingMode(true);
    isRecordingSessionRef.current = true; // Set recording session ref
    
    console.log('Recording mode activated - starting animation and recording...');
    
    // Start animation immediately (same as PLAY button)
    setIsAnimating(true);
    setImageVisible(false);
    setImageOpacity(0);
    setGlyphGrid(null);
    setAnimationPhase('idle');
    animationStartTimeRef.current = Date.now(); // Track start time
    
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
    
    // Start recording BEFORE processing image to ensure we capture everything
    startRecording();
    // startFrameCapture(); // Temporarily disabled PNG recording
    
    // Process image and start animation (same as PLAY button)
    processImageForAnimation();
  };

  const resetAnimation = () => {
    setGlyphGrid(null);
    setAnimationPhase('idle');
    targetGridRef.current = null;
    setIsAnimating(false);
    setRecordingMode(false); // Reset recording mode when animation is reset
    
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
    
    // Stop recording if animation is interrupted
    if (isRecording) {
      console.log('Animation reset, stopping recording...');
      stopRecording();
      // stopFrameCapture(); // Temporarily disabled PNG recording
    }
  };

  const startRecording = () => {
    if (!canvasRef.current) {
      console.warn('Canvas ref not available for recording');
      return;
    }
    
    // Prevent multiple recording sessions
    if (isRecording) {
      console.warn('Recording already in progress, skipping...');
      return;
    }
    
    const canvas = canvasRef.current;
    console.log('Starting recording, canvas dimensions:', canvas.width, 'x', canvas.height);
    
    // Only start recording if canvas has valid dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      console.warn('Canvas has no dimensions, skipping recording');
      return;
    }
    
    // Check if canvas has content
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasContent = imageData.data.some(pixel => pixel !== 0);
    console.log('Canvas has content:', hasContent);
    
    if (!hasContent) {
      console.warn('Canvas has no content, waiting for first frame...');
      setTimeout(startRecording, 100);
      return;
    }
    
    // Check if canvas is properly positioned
    const canvasStyle = window.getComputedStyle(canvas);
    console.log('Canvas position:', canvasStyle.position, 'top:', canvasStyle.top, 'left:', canvasStyle.left);
    
    // Capture the main canvas stream directly
    const stream = canvas.captureStream(60); // 60 FPS for smoother video
    
    // Try different MIME types for better browser compatibility, prioritizing MP4
    let mimeType = 'video/mp4';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }
    }
    
    console.log('Using MIME type:', mimeType);
    
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: mimeType,
      videoBitsPerSecond: 8000000 // 8 Mbps for higher quality
    });
    
    recordedChunksRef.current = [];
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
        console.log('Recording chunk received, size:', event.data.size);
      }
    };
    
    mediaRecorder.onstop = () => {
      console.log('Recording stopped, creating blob...');
      console.log('Recorded chunks:', recordedChunksRef.current.length);
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      console.log('Blob created, size:', blob.size, 'type:', mimeType);
      setRecordingBlob(blob);
      setIsRecording(false);
      setRecordingMode(false); // Reset recording mode after completion
      console.log('✅ Recording complete! Download buttons should now be visible.');
    };
    
    mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event);
    };
    
    mediaRecorder.onstart = () => {
      console.log('MediaRecorder started successfully');
    };
    
    // Store the media recorder reference
    mediaRecorderRef.current = mediaRecorder;
    
    mediaRecorder.start();
    setIsRecording(true);
    console.log('Recording started successfully');
  };

  const stopRecording = () => {
    console.log('stopRecording called, isRecording:', isRecording, 'mediaRecorderRef.current:', !!mediaRecorderRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('Stopping recording, current state:', mediaRecorderRef.current.state);
      try {
        mediaRecorderRef.current.stop();
        console.log('Recording stop() called successfully');
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    } else {
      console.log('Recording not stopped - conditions not met');
      console.log('mediaRecorderRef.current exists:', !!mediaRecorderRef.current);
      if (mediaRecorderRef.current) {
        console.log('mediaRecorder state:', mediaRecorderRef.current.state);
      }
    }
  };

  const downloadRecording = () => {
    if (!recordingBlob) return;
    
    const url = URL.createObjectURL(recordingBlob);
    const a = document.createElement('a');
    a.href = url;
    
    // Determine file extension based on blob type
    let extension = 'mp4';
    if (recordingBlob.type.includes('webm')) {
      extension = 'webm';
    } else if (recordingBlob.type.includes('mp4')) {
      extension = 'mp4';
    }
    
    a.download = `glyph-animation.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const forceStopFrameCapture = () => {
    console.log('Force stopping frame capture...');
    if (capturedFramesRef.current.captureInterval) {
      clearInterval(capturedFramesRef.current.captureInterval);
      capturedFramesRef.current.captureInterval = null;
    }
    setIsCapturingFrames(false);
    isCapturingFramesRef.current = false;
  };

  const startFrameCapture = () => {
    if (!canvasRef.current) {
      console.warn('Canvas ref not available for frame capture');
      return;
    }
    
    // Prevent multiple frame capture sessions
    if (isCapturingFramesRef.current) {
      console.warn('Frame capture already in progress, skipping...');
      return;
    }
    
    console.log('Starting frame capture at 30 FPS...');
    setIsCapturingFrames(true);
    isCapturingFramesRef.current = true; // Set ref immediately
    console.log('Frame capture ref set to:', isCapturingFramesRef.current);
    capturedFramesRef.current = [];
    capturedFramesRef.current.startTime = Date.now();
    capturedFramesRef.current.frameInterval = 1000 / 30; // 30 FPS
    capturedFramesRef.current.captureInterval = null;
    
    // Start a timer-based frame capture that runs at consistent intervals
    capturedFramesRef.current.captureInterval = setInterval(() => {
      console.log('Frame capture interval check - isCapturingFramesRef:', isCapturingFramesRef.current, 'canvasRef:', !!canvasRef.current);
      if (isCapturingFramesRef.current && canvasRef.current) {
        captureFrame();
      } else {
        console.log('Frame capture interval skipped - isCapturingFramesRef:', isCapturingFramesRef.current, 'canvasRef:', !!canvasRef.current);
      }
    }, capturedFramesRef.current.frameInterval);
    
    console.log('Frame capture interval started');
  };

  const captureFrame = () => {
    if (!isCapturingFramesRef.current || !canvasRef.current) {
      console.log('Frame capture skipped - isCapturingFramesRef:', isCapturingFramesRef.current, 'canvasRef:', !!canvasRef.current);
      return;
    }
    
    const canvas = canvasRef.current;
    const frameNumber = capturedFramesRef.current.length;
    const elapsedTime = Date.now() - capturedFramesRef.current.startTime;
    
    console.log(`Capturing frame ${frameNumber} at ${elapsedTime}ms`);
    
    // Create a high-resolution version of the current frame
    const highResCanvas = document.createElement('canvas');
    const scale = 2; // 2x resolution
    highResCanvas.width = canvas.width * scale;
    highResCanvas.height = canvas.height * scale;
    const highResCtx = highResCanvas.getContext('2d');
    highResCtx.scale(scale, scale);
    
    // Draw the current canvas content to the high-res canvas
    highResCtx.drawImage(canvas, 0, 0);
    
    // Convert to PNG blob
    highResCanvas.toBlob((blob) => {
      if (blob) {
        capturedFramesRef.current.push({
          blob,
          frameNumber,
          timestamp: elapsedTime
        });
        console.log(`Frame ${frameNumber} captured successfully, size: ${blob.size}`);
      } else {
        console.warn(`Failed to create blob for frame ${frameNumber}`);
      }
    }, 'image/png', 1.0);
  };

  const stopFrameCapture = () => {
    console.log('stopFrameCapture called, isCapturingFramesRef:', isCapturingFramesRef.current);
    
    // Always clear the interval regardless of state
    if (capturedFramesRef.current.captureInterval) {
      console.log('Clearing frame capture interval');
      clearInterval(capturedFramesRef.current.captureInterval);
      capturedFramesRef.current.captureInterval = null;
    } else {
      console.log('No frame capture interval to clear');
    }
    
    // Always stop frame capture
    setIsCapturingFrames(false);
    isCapturingFramesRef.current = false; // Set ref immediately
    console.log('Frame capture ref cleared to:', isCapturingFramesRef.current);
    console.log(`Frame capture stopped. Captured ${capturedFramesRef.current.length} frames.`);
    setCapturedFrames([...capturedFramesRef.current]);
  };

  const downloadPNGSequence = () => {
    if (capturedFrames.length === 0) return;
    
    // Create a zip file containing all PNG frames
    const JSZip = window.JSZip;
    if (!JSZip) {
      alert('JSZip library not loaded. Please refresh the page and try again.');
      return;
    }
    
    const zip = new JSZip();
    const framesFolder = zip.folder('glyph-frames');
    
    capturedFrames.forEach((frame, index) => {
      const fileName = `frame_${String(index).padStart(4, '0')}.png`;
      framesFolder.file(fileName, frame.blob);
    });
    
    zip.generateAsync({ type: 'blob' }).then((content) => {
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'glyph-animation-frames.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  useEffect(() => {
    if (glyphGrid) {
      drawToBufferAndBlit(glyphGrid);
    }
  }, [glyphGrid]);

  // Debug effect to track recording blob
  useEffect(() => {
    if (recordingBlob) {
      console.log('🎉 Recording blob set successfully! Size:', recordingBlob.size);
    }
  }, [recordingBlob]);

  // Process image when it changes
  useEffect(() => {
    // Reset states
    setGlyphGrid(null);
    setAnimationPhase('idle');
    targetGridRef.current = null;
    setIsAnimating(false);
    setImageVisible(false);
    
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }

    if (!imageUrl) return;

    // Process the image automatically
    processImage();
  }, [imageUrl]);

  // Reprocess image when contrast changes
  useEffect(() => {
    if (!imageUrl || !targetGridRef.current) return;
    
    // Hide the original image when adjusting contrast
    setImageVisible(false);
    
    // Reset animation state so play button works after contrast adjustment
    setIsAnimating(false);
    setRecordingMode(false); // Reset recording mode when contrast changes
    
    // Just update the target grid with new contrast, don't start animation
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      const targetGrid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          let brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          
          // Apply contrast adjustment
          brightness = ((brightness / 255 - 0.5) * contrast + 0.5) * 255;
          brightness = Math.max(0, Math.min(255, brightness));
          
          const index = glyphIds.length - 1 - Math.floor((brightness / 255) * (glyphIds.length - 1));
          row.push(index);
        }
        targetGrid.push(row);
      }

      // Update target grid and show final result immediately
      targetGridRef.current = targetGrid;
      setGlyphGrid(targetGrid);
    };

    img.onload = tryProcess;
    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
    };
  }, [contrast]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      // Clean up frame capture interval
      forceStopFrameCapture();
    };
  }, []);

  return (
    <div 
      className={`glyph-page ${isDragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Top control bar */}
      <div className="glyph-top-bar">
        <div className="top-bar-content">
          <div 
            className="upload-button"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <label htmlFor="image-upload" className="upload-label">
              <span>📁 Upload Image</span>
            </label>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
          
          <button
            className="btn btn-play"
            onClick={startAnimation}
            disabled={!imageUrl || isAnimating}
          >
            ▶
          </button>
          
          <button
            className={`btn ${recordingMode ? 'btn-record-active' : 'btn-record'}`}
            onClick={startRecordingAnimation}
            disabled={!imageUrl || isAnimating || isRecording}
          >
            {recordingMode ? '🔴' : '⚫'}
          </button>
          
          <label className="image-toggle">
            <input
              type="checkbox"
              checked={showImage}
              onChange={(e) => setShowImage(e.target.checked)}
            />
            <span>Show Image</span>
          </label>
          
          <div className="contrast-control">
            <label>Contrast</label>
            <input
              type="range"
              min="0.1"
              max="3.0"
              step="0.1"
              value={contrast}
              onChange={(e) => setContrast(parseFloat(e.target.value))}
              className="contrast-slider"
            />
            <span className="contrast-value">{contrast.toFixed(1)}</span>
          </div>
          
          <div className="recording-status">
            {isRecording && (
              <span className="status-recording">🔴 Recording...</span>
            )}
            {recordingMode && !isRecording && !recordingBlob && !capturedFrames.length && (
              <span className="status-ready">⚪ Recording in progress...</span>
            )}
            {!recordingMode && !isRecording && !recordingBlob && !capturedFrames.length && (
              <span className="status-ready">⚪ Press ⚫ to record</span>
            )}
            {recordingBlob && (
              <div className="download-options">
                <div className="download-ready-indicator">✅ Ready!</div>
                <button
                  className="btn btn-download"
                  onClick={downloadRecording}
                >
                  📥 MP4
                </button>
                {capturedFrames.length > 0 && (
                  <button
                    className="btn btn-download-png"
                    onClick={downloadPNGSequence}
                  >
                    🖼️ PNG Sequence ({capturedFrames.length} frames)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="glyph-page-container">
        <div className="glyph-page-content">
          <div className="glyph-display">
            {imageUrl ? (
              <div className="image-container">
                <img
                  ref={activeImageRef}
                  src={imageUrl}
                  alt="Uploaded image"
                  className="uploaded-image"
                  loading="lazy"
                  style={{
                    filter: !imageVisible ? 'grayscale(100%)' : 'none',
                    opacity: !imageVisible ? imageOpacity : (showImage ? 1 : 0),
                    transition: !imageVisible && imageOpacity === 0 ? 'none' : `opacity ${CROSSFADE_DURATION}ms ease-in, filter ${CROSSFADE_DURATION}ms ease-in`,
                  }}
                />
                <canvas
                  ref={canvasRef}
                  className="glyph-canvas"
                  style={canvasStyle}
                />
              </div>
            ) : (
              <div className="placeholder">
                <div className="placeholder-content">
                  <span>📷</span>
                  <p>Upload an image to get started</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 